import React from "react";
import Grid from "./Grid";
import styled, { css } from "styled-components";
import SafeSiteImage, { transformImageUrl } from "./SafeSiteImage";
import Section from "./Section";
import Container from "./Container";
import { HtmlContent } from "./Content";
import { graphql, useStaticQuery } from "gatsby";
import { Carousel } from "./Carousel";

const MediaCardStyles = styled.div`
  ${({ ...props }) => css`
    text-align: ${props.$textAlign};
    img,
    video {
      border-radius: 4px;
      width: 100%;
      aspect-ratio: 4/3;
      height: auto;
      object-fit: cover;
    }
    h3 {
      margin-top: 24px;
    }
    p {
      margin-top: 12px;
    }
    .button {
      margin-top: 16px;
    }
    .swiper-wrapper {
      height: auto;
    }
    .image-container {
      border-radius: 4px;
      display: block;
      overflow: hidden;
    }
    img {
      transition: transform .5s ease-in;
    }
    &:hover:not(.disableZoom) {
      img {
        transform: scale(1.1)
      }
    }
  `}
`

const StyledGridContainer = styled(Container)`
  ${({ ...props }) => css`
    ul {
      justify-content: ${props.$justifyContent}
    }
  `}
`

export const Media = ({ mobileColumns = 1, columns = 4, textAlign = 'left', media, background, justifyContent = 'left', zoom = false }) => {
  let site = {
    siteMetadata: {
      siteUrl: 'https://safesitefacilities.co.uk'
    }
  }
  if (!process.env.STORYBOOK) {
    ({ site } = useStaticQuery(graphql`
      query SiteUrlQuery {
        site {
          siteMetadata {
            siteUrl
          }
        }
      }
    `))
  }

  const mediaComponent = ({
    mediaType = 'image',
    image,
    imageLink,
    video,
    videoPoster,
    carouselImages,
    autoScroll,
    delay
  }) => {
    switch (mediaType) {
      case 'image':
        const imageComponent = <span className="image-container"><SafeSiteImage image={image.mediaItemUrl} alt={image.altText} width="720" /></span>

        if (imageLink) {
          return (<a href={imageLink.url} target={imageLink.target} title={imageLink.title}>
            {imageComponent}
          </a>)
        }

        return imageComponent
      case 'video':
        return <video autoPlay muted loop playsInline poster={transformImageUrl({siteUrl: site.siteMetadata.siteUrl, imageUrl: videoPoster?.mediaItemUrl, width: 1920 })}><source src={video.publicUrl}></source></video>

      case 'carousel':
        return <Carousel
          images={carouselImages}
          fullWidth={true}
          autoScroll={autoScroll}
          delay={delay}
        />
    }
  }

  const title = (title, titleLink) => {
    const titleComponent = <h3><HtmlContent htmlString={title} /></h3>

    if (titleLink) {
      return (<a href={titleLink.url} target={titleLink.target} title={titleLink.title}>
        {titleComponent}
      </a>)
    }

    return titleComponent
  }

  return (
    <Section grey={background === 'grey'}>
      <StyledGridContainer $justifyContent={justifyContent}>
        <Grid as="ul" equalHeight>
          {media
            .map((item, index) => {
              return (
                <Grid item as="li" xs={mobileColumns === 2 ? 6 : 12} sm={6} md={columns === 4 ? 4 : 12 / columns} lg={12 / columns} key={index}>
                  <MediaCardStyles
                    $textAlign={textAlign}
                    className={zoom ? '' : 'disableZoom'}
                  >
                    {mediaComponent(item)}
                    {item.title && title(item.title, item.titleLink)}
                    {item.content && <p><HtmlContent htmlString={item.content} /></p>}
                    {item.button?.url && item.button?.title && (
                      <a href={item.button.url} target={item.button.target} className="button"><HtmlContent htmlString={item.button.title} /></a>
                    )}
                  </MediaCardStyles>
                </Grid>
              );
            })}
        </Grid>
      </StyledGridContainer>
    </Section>
  );
};
